import { DELEGATED_PROMPT_PREFIX, DELEGATION_REPLY_PREFIX } from '@/api/constants'

/**
 * One agent asking another leaves two messages behind: the question in the
 * child session, and the answer in the session that asked. Both are user-role
 * rows the PLATFORM composed, and both carry a machine-readable envelope so two
 * different readers can take what they need from one message — the model reads
 * the whole line as context, the UI reads the attributes.
 *
 * Reading the attributes is what lets the UI render these ATTRIBUTED and in the
 * nurse's own language. Not every attribute is for the UI: the envelope also
 * carries `conversation` and `produced`, which exist so the ASKING AGENT can
 * continue a consult and say a plan was made without describing one, and are
 * deliberately not surfaced here — a field the card does not render would read
 * as a contract with the UI that is not one. `failed` IS surfaced: a consult
 * that could not answer has no body, and the card says so in her language. Without it the only options are showing her the
 * platform's English prose in a bubble that looks like something she typed, or
 * hiding the message and leaving her reading an answer to a question that
 * appears nowhere.
 */
export type DelegationKind = 'delegated' | 'delegation_reply'

export interface DelegationMessage {
  kind: DelegationKind
  /** For a question, the agent that asked. For a reply, the agent that answered. */
  agent: string
  /** The consult session the answer lives in. Replies only. */
  sessionId?: string
  /** Why the consult could not answer, when it could not. Replies only. */
  failed?: string
  /** The message without its envelope: the question, or the answer. */
  body: string
}

/**
 * Envelope: `<prefix>agent="<slug>"[ session="<id>"][ conversation="<handle>"][ produced="<kinds>"][ failed="<reason>"]] <body>`,
 * the grammar tricorder-go/chat/delegation_envelope.go encodes, where the prefix
 * is one of the two constants above — built FROM them, so a rename on the Go
 * side that updates the constants updates the parser with it instead of leaving
 * a second literal to drift.
 *
 * Attributes are quoted, so the header ends at the first `"] ` and a body that
 * contains a bracket of its own (`see table [3]`) survives intact. Anchored at
 * the start, so a nurse writing the token mid-sentence never matches.
 */
const KIND_BY_PREFIX: Record<string, DelegationKind> = {
  [DELEGATED_PROMPT_PREFIX]: 'delegated',
  [DELEGATION_REPLY_PREFIX]: 'delegation_reply',
}

// Longest first: today the two prefixes cannot shadow each other, but a future
// kind sharing a stem (`[delegated action `) would match the shorter one and
// leave its own word in the attribute run. Sorted once, not per call.
const PREFIXES_LONGEST_FIRST = Object.keys(KIND_BY_PREFIX).sort((a, b) => b.length - a.length)

const ATTRIBUTES_AND_BODY = /^((?:\w+="[^"]*" ?)+)\] ([\s\S]*)$/

/** Every `name="value"` pair in the attribute run, in one pass. */
const ATTRIBUTE = /(\w+)="([^"]*)"/g

/**
 * Reads the whole attribute run into a map in ONE pass.
 *
 * Per-name lookup meant compiling a fresh RegExp for each attribute, four per
 * message, on every render of every user message in a transcript.
 */
function attributes(attrs: string): Record<string, string> {
  return Object.fromEntries([...attrs.matchAll(ATTRIBUTE)].map((m) => [m[1], m[2]]))
}

/**
 * parseDelegationMessage returns the delegation this message carries, or null
 * when it carries none.
 *
 * Null for anything malformed, deliberately: half a header is not evidence of
 * who asked or answered, and putting an agent's name on a message whose origin
 * cannot be read is worse than rendering it plainly.
 */
export function parseDelegationMessage(content: string): DelegationMessage | null {
  const prefix = PREFIXES_LONGEST_FIRST.find((p) => content.startsWith(p))
  if (!prefix) return null
  const match = ATTRIBUTES_AND_BODY.exec(content.slice(prefix.length))
  if (!match) return null
  const [, attrs, body] = match
  const attr = attributes(attrs)
  if (!attr.agent) return null
  return {
    kind: KIND_BY_PREFIX[prefix],
    agent: attr.agent,
    sessionId: attr.session,
    failed: attr.failed,
    body,
  }
}
